import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";
import util from "../util/util";
import Tooltip from "../tooltip/tooltip";

const jQuery = window.jQuery;

export default function Settings(props) {
  const [dropbox, setDropbox] = useState(null);
  const [dropboxUrl, setDropboxUrl] = useState("");
  const [stationId, setStationId] = useState("");
  const [defaultStationLogo, setDefaultStationLogo] = useState("");
  const [defaultStationAddress1, setDefaultStationAddress1] = useState("");
  const [defaultStationAddress2, setDefaultStationAddress2] = useState("");
  const [defaultStationTelephone, setDefaultStationTelephone] = useState("");
  const [defaultStationWebsite, setDefaultStationWebsite] = useState("");
  const [defaultCustomFooter, setDefaultCustomFooter] = useState("");
  const [customizationsId, setCustomizationsId] = useState(0);
  const [customStationLogo, setCustomStationLogo] = useState("");
  const [customStationAddress1, setCustomStationAddress1] = useState("");
  const [customStationAddress2, setCustomStationAddress2] = useState("");
  const [customStationTelephone, setCustomStationTelephone] = useState("");
  const [customStationWebsite, setCustomStationWebsite] = useState("");
  const [customCustomFooter, setCustomCustomFooter] = useState("");
  const [showCustomizationsForm, setShowCustomizationsForm] = useState(false);
  const [uploadingStationLogo, setUploadingStationLogo] = useState(false);
  const [uploadingProgress, setUploadingProgress] = useState(0);
  const [savingCustomizations, setSavingCustomizations] = useState(false);
  const [deletingCustomizations, setDeletingCustomizations] = useState(false);

  useEffect(() => {
    getIntegrations();
    getInvoiceCustomizations();
  }, [props.params]);

  // get a list of the station integrations;
  // if a provider is supported, display it;
  const getIntegrations = () => {
    util
      .getIntegrations()
      .then(data => {
        data.forEach(integration => {
          if (integration.name === `dropbox`) {
            setDropboxUrl(integration.url);
          }
        });

        return util.getStationIntegrations();
      })
      .then(data => {
        data.results.forEach(integration => {
          if (integration.provider === `dropbox`) {
            setDropbox({
              id: integration.id,
              name: integration.provider_payload.account.display_name,
              email: integration.provider_payload.account.email,
              avatar: integration.provider_payload.account.photo_url
            });
          }
        });
      });
  };

  // get the Station data, from Station Manager or a custom inputted one:
  // making the call for the Custom data first, and if that doesn't exist,
  // making another call for the default Station data (easier in the backend like this)
  const getInvoiceCustomizations = () => {
    util.getInvoiceCustomizations().then(data => {
      const customizations = data.results[0];

      if (customizations) {
        setStationId(customizations.station);
        setDefaultStationLogo(customizations.station_logo);
        setDefaultStationAddress1(customizations.station_address_1);
        setDefaultStationAddress2(customizations.station_address_2);
        setDefaultStationTelephone(customizations.station_telephone);
        setDefaultStationWebsite(customizations.station_website);
        setDefaultCustomFooter(customizations.custom_footer);
        setCustomizationsId(customizations.id);
        setCustomStationLogo("");
        setCustomStationAddress1(customizations.station_address_1);
        setCustomStationAddress2(customizations.station_address_2);
        setCustomStationTelephone(customizations.station_telephone);
        setCustomStationWebsite(customizations.station_website);
        setCustomCustomFooter(customizations.custom_footer);
      } else {
        util.getInvoiceDefaults().then(data => {
          setStationId(data.station);
          setDefaultStationLogo(data.station_logo);
          setDefaultStationAddress1(data.station_address_1);
          setDefaultStationAddress2(data.station_address_2);
          setDefaultStationTelephone(data.station_telephone);
          setDefaultStationWebsite(data.station_website);
          setDefaultCustomFooter(data.custom_footer);
          setCustomizationsId(0);
          setCustomStationLogo("");
          setCustomStationAddress1(data.station_address_1);
          setCustomStationAddress2(data.station_address_2);
          setCustomStationTelephone(data.station_telephone);
          setCustomStationWebsite(data.station_website);
          setCustomCustomFooter(data.custom_footer);
        });
      }
    });
  };

  // remove the association between this station and a dropbox account;
  const deleteDropboxIntegration = id => {
    const title = `Remove Dropbox Integration`;
    const body = `<p>Are you sure you want to remove the Dropbox Integration for this station?</p>`;
    const buttonTextDefault = `Remove the Integration`;
    const buttonTextLoading = `Removing the Integration`;
    const iconClass = `fa-trash-o`;
    const $modal = util.renderModal(
      title,
      body,
      buttonTextDefault,
      buttonTextLoading,
      iconClass
    );
    const $confirmButton = $modal.find(`.btn-danger`);

    // remove the exception on confirmation of the modal;
    $confirmButton.one(`click`, () => {
      $confirmButton.button(`loading`);

      util
        .deleteDropboxIntegration(id)
        .fail(() => {
          setTimeout(() => {
            // reset the button on fail so it can be clicked again;
            $confirmButton.button(`reset`);
          }, util.delayForm);
        })
        .then(() => {
          setTimeout(() => {
            setDropbox(null);
            $modal.modal(`hide`);
          }, util.delayForm);
        });
    });

    // attach the modal to the DOM, and then show it;
    jQuery(`body`).append($modal);
    $modal.modal(`show`);
  };

  // removing the Invoice Customizations
  const deleteCustomizations = () => {
    // put the Delete button into a processing state;
    setDeletingCustomizations(true);

    // deleting the existing Customizations
    util.deleteInvoiceCustomizations(customizationsId).then(() => {
      // on success, put the button back and revert to the default values
      setDeletingCustomizations(false);
      getInvoiceCustomizations();
    });
  };

  // uploading a new Station Logo image
  const onStationLogoDrop = files => {
    const file = files[0];

    // fail safe
    if (!file) {
      return false;
    }

    // put the Save buttons into a disabled state until the upload is finished
    // and reset the progress indicator;
    setUploadingStationLogo(true);
    setUploadingProgress(0);

    // logoUrl isn't known yet, it will be determined in the response
    // from the Django server
    let logoUrl = ``;

    // getting the S3 pre-signed POST url
    const fileMetadata = {
      filename: file.name,
      filetype: file.type
    };
    util.getS3Url(fileMetadata).then(preSignedPost => {
      const s3Data = new FormData();
      const s3Url = preSignedPost.data.url;

      // saving the logoUrl (the s3 location created by Django,
      // where we'll then upload the image file)
      logoUrl = preSignedPost.url;

      // creating the data to be sent to s3 (Django pre-signed fields + image file)
      // - including all the 'pre-signed' fields
      for (const key in preSignedPost.data.fields) {
        s3Data.append(key, preSignedPost.data.fields[key]);
      }
      // - including the file
      s3Data.append(`file`, file);

      // add a progress callback;
      const progressCallback = () => {
        const xhr = new window.XMLHttpRequest();

        xhr.upload.addEventListener(
          `progress`,
          progressEvent => {
            if (progressEvent.lengthComputable) {
              const percentComplete = Math.ceil(
                (progressEvent.loaded / progressEvent.total) * 100
              );

              // update the progress indicator;
              setUploadingProgress(percentComplete);
            }
          },
          false
        );

        return xhr;
      };

      // uploading to s3
      util.saveFileToS3(s3Data, s3Url, progressCallback).then(() => {
        // on successful upload, saving the s3 url, and re-enabling the Save button
        setCustomStationLogo(logoUrl);
        setUploadingStationLogo(false);
      });
    });
  };

  // removing the custom customizations the user added
  const cancelCustomizations = () => {
    setShowCustomizationsForm(false);
    setCustomStationLogo("");
    setCustomStationAddress1(defaultStationAddress1);
    setCustomStationAddress2(defaultStationAddress2);
    setCustomStationTelephone(defaultStationTelephone);
    setCustomStationWebsite(defaultStationWebsite);
    setCustomCustomFooter(defaultCustomFooter);
  };

  // saving the Invoice Customizations
  const saveCustomizations = () => {
    // put the Save buttons into a processing state;
    setSavingCustomizations(true);

    // preparing the data to sent to the backend
    const customizations = {
      id: customizationsId,
      station: stationId,
      station_logo: customStationLogo || defaultStationLogo,
      station_address_1: customStationAddress1,
      station_address_2: customStationAddress2,
      station_telephone: customStationTelephone,
      station_website: customStationWebsite,
      custom_footer: customCustomFooter
    };

    // saving the data (Post for Create or Put for Update)
    util
      .saveInvoiceCustomizations(customizations, customizationsId)
      .then(data => {
        // on successful Save updating the State with the inputted values
        // and clearing the form
        setStationId(data.station);
        setDefaultStationLogo(data.station_logo);
        setDefaultStationAddress1(data.station_address_1);
        setDefaultStationAddress2(data.station_address_2);
        setDefaultStationTelephone(data.station_telephone);
        setDefaultStationWebsite(data.station_website);
        setDefaultCustomFooter(data.custom_footer);
        setCustomizationsId(data.id);
        setCustomStationLogo("");
        setCustomStationAddress1(data.station_address_1);
        setCustomStationAddress2(data.station_address_2);
        setCustomStationTelephone(data.station_telephone);
        setCustomStationWebsite(data.station_website);
        setCustomCustomFooter(data.custom_footer);
        setShowCustomizationsForm(false);
        setSavingCustomizations(false);
      });
  };

  return (
    <div>
      <div className="row">
        <div className="col-xs-6">
          <h4 className="page__title">Settings</h4>
        </div>
      </div>

      <hr />

      <div className="row">
        <div className="col-sm-8">
          {dropbox ? (
            <div>
              <h4 className="page__header">Dropbox Integration</h4>
              {dropbox.avatar ? (
                <div className="settings__avatar">
                  <div className="settings__avatar--photo">
                    <p>
                      <img src={dropbox.avatar} alt="" />
                    </p>
                  </div>
                  <div className="settings__avatar--name">
                    <p>{dropbox.name}</p>
                    <p>{dropbox.email}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p>{dropbox.name}</p>
                  <p>{dropbox.email}</p>
                </div>
              )}

              <p>
                <button
                  className="btn btn-danger"
                  onClick={() => deleteDropboxIntegration(dropbox.id)}
                >
                  <i className="fa fa-trash-o" aria-hidden="true"></i>
                  Remove Dropbox Integration
                </button>
              </p>
            </div>
          ) : (
            <div>
              <h4 className="page__header">Dropbox Integration</h4>

              <p>
                <a
                  className="btn btn-primary"
                  href={`${dropboxUrl}?next=${util.returnSettingsUrl()}`}
                >
                  Log In to Dropbox
                </a>
              </p>
            </div>
          )}
          {util.spotsAvailable() ? (
            <div>
              <p>
                Integration with Dropbox will create a special folder in your
                Dropbox account, &ldquo;PBS KIDS AIM&rdquo;. This folder will
                contain two sub-folders called &ldquo;pods&rdquo; and
                &ldquo;spots&rdquo;.
              </p>
              <p>
                Placing interstitial files in these folders will automatically
                upload them and create a pod or a spot with the same name as the
                file name. The file will then be moved to the
                &ldquo;complete&rdquo; or &ldquo;failed&rdquo; subfolder in your
                Dropbox folder.
              </p>
            </div>
          ) : (
            <p>
              Integration with Dropbox will create a special folder in your
              Dropbox account, &ldquo;PBS KIDS AIM Pods&rdquo;. Placing
              interstitial files in this folder will automatically upload them
              and create a pod with the same name as the file name. The file
              will then be removed from your Dropbox folder.
            </p>
          )}
        </div>
      </div>

      <hr />

      <div className="row">
        <div className={!showCustomizationsForm ? "col-sm-8" : "hidden"}>
          <div className="row">
            <div className="col-sm-5">
              <h4 className="page__title">Invoice Customizations</h4>
            </div>
            <div className="col-sm-7">
              <div className="page__actions">
                <button
                  id="edit_customizations"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCustomizationsForm(true);
                  }}
                >
                  <i className="fa fa-pencil" aria-hidden="true"></i>
                  Edit
                </button>
                <span
                  data-tip="Default settings cannot be deleted.<br />
                            Click Edit to change any default fields."
                  data-for="deleteCustomization"
                >
                  <button
                    id="delete_customizations"
                    className="btn btn-danger"
                    onClick={() => deleteCustomizations()}
                    disabled={customizationsId === 0 ? true : ""}
                  >
                    {deletingCustomizations ? (
                      <span>
                        <i className="fa fa-spinner fa-spin"></i>
                        <i className="fa fa-trash-o"></i>
                        Deleting
                      </span>
                    ) : (
                      <span>
                        <i className="fa fa-trash-o" aria-hidden="true"></i>
                        Delete
                      </span>
                    )}
                  </button>
                </span>
                {customizationsId === 0 && <Tooltip id="deleteCustomization" />}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3">
              <p className="page__header settings__row-m-t">
                Station Logo: &nbsp;
                <i
                  data-tip="By default, the invoice uses the color cobranded logo in
                  Station Manager.<br />Upload a new logo file here to override it."
                  data-for="stationLogo"
                  className="glyphicon glyphicon-question-sign"
                  aria-hidden="true"
                ></i>
              </p>
              <img src={defaultStationLogo} className="img-thumbnail" alt="" />
              <Tooltip id="stationLogo" offset_top={"-6"} />
            </div>
          </div>
          <p className="page__header settings__row-m-t">
            Station Address: &nbsp;
            <i
              data-tip="By default, the invoice uses the contact details address
              listed in Station Manager.<br />Enter an address here to override it."
              data-for="stationAddress"
              className="glyphicon glyphicon-question-sign"
              aria-hidden="true"
            ></i>
          </p>
          <Tooltip id="stationAddress" offset_top={"-6"} />
          <p>{defaultStationAddress1}</p>
          <p>{defaultStationAddress2}</p>
          <p>{defaultStationTelephone}</p>
          <p>{defaultStationWebsite}</p>
          <p className="page__header settings__row-m-t">
            Custom Footer: &nbsp;
            <i
              data-tip="By default, the invoice includes a templated
              footer.<br />Enter text here to override it."
              data-for="defaultCustomFooter"
              className="glyphicon glyphicon-question-sign"
              aria-hidden="true"
            ></i>
          </p>
          <Tooltip id="defaultCustomFooter" offset_top={"-6"} />
          <p className="settings__linebreak">{defaultCustomFooter}</p>
        </div>

        <div className={showCustomizationsForm ? "col-sm-8" : "hidden"}>
          <div className="row">
            <div className="col-sm-5">
              <h4 className="page__title">Edit Invoice Customizations</h4>
            </div>
            <div className="col-sm-7">
              <p className="page__actions">
                <button
                  id="cancel_customizations"
                  className="btn btn-default"
                  onClick={() => cancelCustomizations()}
                >
                  Cancel
                </button>
                <button
                  id="save_customizations"
                  className="btn btn-primary"
                  onClick={() => saveCustomizations()}
                  disabled={uploadingStationLogo ? true : ""}
                >
                  {savingCustomizations ? (
                    <span>
                      <i className="fa fa-spinner fa-spin"></i>
                      <i className="fa fa-floppy-o"></i>
                      Saving Customizations
                    </span>
                  ) : (
                    <span>
                      <i className="fa fa-floppy-o" aria-hidden="true"></i>
                      Save Customizations
                    </span>
                  )}
                </button>
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-5">
              <p className="page__header settings__row-m-t">
                Station Logo: &nbsp;
                <i
                  data-tip="By default, the invoice uses the color cobranded logo in
                  Station Manager.<br />Upload a new logo file here to override it."
                  data-for="stationLogo"
                  className="glyphicon glyphicon-question-sign"
                  aria-hidden="true"
                ></i>
              </p>
              <Tooltip id="stationLogo" offset_top={"-6"} />
              <div className="settings__station-logo">
                <Dropzone
                  className="form-control"
                  multiple={false}
                  onDrop={files => onStationLogoDrop(files)}
                >
                  <div
                    className="settings__station-logo-dropzone"
                    dangerouslySetInnerHTML={{
                      __html:
                        customStationLogo ||
                        "Drop .jpeg, .png,… file to attach, or browse."
                    }}
                  ></div>
                </Dropzone>
                {uploadingStationLogo ? (
                  <div className="progress settings__station-logo-progressbar">
                    <div
                      className="progress-bar progress-bar-striped active"
                      role="progressbar"
                      aria-valuenow={uploadingProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ width: `${uploadingProgress}%` }}
                    >
                      {uploadingProgress}% Complete
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-5">
              <p className="page__header settings__row-m-t">
                Station Address: &nbsp;
                <i
                  data-tip="By default, the invoice uses the contact details address
                  listed in Station Manager.<br />Enter an address here to override it."
                  data-for="stationAddress"
                  className="glyphicon glyphicon-question-sign"
                  aria-hidden="true"
                ></i>
              </p>
              <Tooltip id="stationAddress" offset_top={"-6"} />
              <div className="form-group">
                <label className="control-label" htmlFor="street_address">
                  Street Address
                </label>
                <input
                  type="text"
                  id="street_address"
                  className="form-control"
                  maxLength="200"
                  autoFocus
                  value={customStationAddress1}
                  onChange={event => {
                    setCustomStationAddress1(event.target.value);
                  }}
                ></input>
              </div>
              <div className="form-group">
                <label className="control-label" htmlFor="city_zipcode">
                  City, State, Zipcode
                </label>
                <input
                  type="text"
                  id="city_zipcode"
                  className="form-control"
                  maxLength="200"
                  autoFocus
                  value={customStationAddress2}
                  onChange={event => {
                    setCustomStationAddress2(event.target.value);
                  }}
                ></input>
              </div>
              <div className="form-group">
                <label className="control-label" htmlFor="telephone">
                  Telephone
                </label>
                <input
                  type="text"
                  id="telephone"
                  className="form-control"
                  maxLength="200"
                  autoFocus
                  value={customStationTelephone}
                  onChange={event => {
                    setCustomStationTelephone(event.target.value);
                  }}
                ></input>
              </div>
              <div>
                <label className="control-label" htmlFor="website">
                  Website
                </label>
                <input
                  type="text"
                  id="website"
                  className="form-control"
                  maxLength="200"
                  autoFocus
                  value={customStationWebsite}
                  onChange={event => {
                    setCustomStationWebsite(event.target.value);
                  }}
                ></input>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-5">
              <p className="page__header settings__row-m-t">
                Custom Footer: &nbsp;
                <i
                  data-tip="By default, the invoice includes a templated
                  footer.<br />Enter text here to override it."
                  data-for="customCustomFooter"
                  className="glyphicon glyphicon-question-sign"
                  aria-hidden="true"
                ></i>
              </p>
              <Tooltip id="customCustomFooter" offset_top={"-6"} />
              <textarea
                id="street_address"
                className="form-control settings__linebreak"
                maxLength="500"
                autoFocus
                value={customCustomFooter}
                onChange={event => {
                  setCustomCustomFooter(event.target.value);
                }}
              ></textarea>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <p className="page__actions">
                <button
                  id="cancel_customizations"
                  className="btn btn-default"
                  onClick={() => cancelCustomizations()}
                >
                  Cancel
                </button>
                <button
                  id="save_customizations"
                  className="btn btn-primary"
                  onClick={() => saveCustomizations()}
                  disabled={uploadingStationLogo ? true : ""}
                >
                  {savingCustomizations ? (
                    <span>
                      <i className="fa fa-spinner fa-spin"></i>
                      <i className="fa fa-floppy-o"></i>
                      Saving Customizations
                    </span>
                  ) : (
                    <span>
                      <i className="fa fa-floppy-o" aria-hidden="true"></i>
                      Save Customizations
                    </span>
                  )}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Settings.propTypes = {
  params: PropTypes.object
};
